/**
 * Viewport Manager
 * Handles viewport transformation, culling, and dirty rectangle optimization
 */

import type { Viewport, Transform, BoundingBox, CanvasElement } from './types'

export class ViewportManager {
  private viewport: Viewport
  private transform: Transform
  private canvasWidth: number
  private canvasHeight: number

  constructor(canvasWidth: number, canvasHeight: number) {
    this.canvasWidth = canvasWidth
    this.canvasHeight = canvasHeight
    this.viewport = {
      x: 0,
      y: 0,
      width: canvasWidth,
      height: canvasHeight,
      scale: 1,
    }
    this.transform = {
      translate: { x: 0, y: 0 },
      scale: 1,
    }
  }

  /**
   * Update viewport dimensions (on canvas resize)
   */
  updateDimensions(width: number, height: number): void {
    this.canvasWidth = width
    this.canvasHeight = height
    this.viewport.width = width
    this.viewport.height = height
  }

  /**
   * Apply transform (pan/zoom) to viewport
   */
  applyTransform(dx: number, dy: number, scale?: number): void {
    this.transform.translate.x += dx
    this.transform.translate.y += dy

    if (scale !== undefined) {
      this.transform.scale = scale
    }

    this.updateViewport()
  }

  /**
   * Set transform directly
   */
  setTransform(x: number, y: number, scale: number): void {
    this.transform.translate.x = x
    this.transform.translate.y = y
    this.transform.scale = scale
    this.updateViewport()
  }

  /**
   * Get current transform
   */
  getTransform(): Transform {
    return { ...this.transform }
  }

  /**
   * Update viewport bounds based on transform
   */
  private updateViewport(): void {
    const { translate, scale } = this.transform

    // Calculate viewport in world coordinates
    this.viewport.x = -translate.x / scale
    this.viewport.y = -translate.y / scale
    this.viewport.width = this.canvasWidth / scale
    this.viewport.height = this.canvasHeight / scale
    this.viewport.scale = scale
  }

  /**
   * Check if element intersects with viewport (for culling)
   */
  isElementVisible(element: CanvasElement): boolean {
    const { x, y, width, height } = this.viewport
    const { position, size } = element

    // Element bounding box
    const ex = position.x
    const ey = position.y
    const ew = size.width
    const eh = size.height

    // AABB intersection test
    return !(
      ex + ew < x ||
      ex > x + width ||
      ey + eh < y ||
      ey > y + height
    )
  }

  /**
   * Filter elements by viewport visibility
   */
  cullElements(elements: CanvasElement[]): CanvasElement[] {
    return elements.filter(el => this.isElementVisible(el))
  }

  /**
   * Get viewport bounds
   */
  getViewport(): Viewport {
    return { ...this.viewport }
  }

  /**
   * Fit content to viewport
   * Similar to JointJS scaleContentToFit
   */
  fitContentToView(
    contentBounds: BoundingBox,
    options: {
      padding?: number
      maxScale?: number
      minScale?: number
    } = {}
  ): void {
    const { padding = 30, maxScale = 4, minScale = 0.1 } = options

    // Calculate scale to fit content
    const scaleX = (this.canvasWidth - padding * 2) / contentBounds.width
    const scaleY = (this.canvasHeight - padding * 2) / contentBounds.height
    let scale = Math.min(scaleX, scaleY)

    // Clamp scale
    scale = Math.max(minScale, Math.min(maxScale, scale))

    // Center content in viewport
    const contentCenterX = contentBounds.x + contentBounds.width / 2
    const contentCenterY = contentBounds.y + contentBounds.height / 2

    const translateX = this.canvasWidth / 2 - contentCenterX * scale
    const translateY = this.canvasHeight / 2 - contentCenterY * scale

    this.setTransform(translateX, translateY, scale)
  }

  /**
   * 한 객체를 화면 중앙에 크게 보여 준다(객체 수정 시트에서 행을 골랐을 때).
   *
   * 확대율은 대상이 화면 짧은 변의 1/fillDivisor 만큼 차지하도록 잡되,
   *   - 이미 그보다 더 확대돼 있으면 그대로 둔다(사용자가 들여다보던 배율을 뺏지 않는다)
   *   - maxScale 을 넘지 않는다(휠 줌 상한과 동일)
   * fitContentToView 와 달리 축소는 하지 않는다 — 목록에서 고른 객체를 "찾아가는"
   * 동작이라 화면이 갑자기 멀어지면 어디를 보고 있었는지 잃는다.
   */
  focusOnBounds(
    bounds: BoundingBox,
    options: { fillDivisor?: number; maxScale?: number } = {}
  ): void {
    const { fillDivisor = 8, maxScale = 4 } = options
    const span = Math.max(bounds.width, bounds.height, 1)
    const fitScale = Math.min(this.canvasWidth, this.canvasHeight) / (span * fillDivisor)
    const scale = Math.min(maxScale, Math.max(this.transform.scale, fitScale))

    const centerX = bounds.x + bounds.width / 2
    const centerY = bounds.y + bounds.height / 2
    this.setTransform(
      this.canvasWidth / 2 - centerX * scale,
      this.canvasHeight / 2 - centerY * scale,
      scale
    )
  }

  /**
   * Calculate bounding box of all elements
   */
  calculateBounds(elements: CanvasElement[]): BoundingBox | null {
    if (elements.length === 0) return null

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    for (const el of elements) {
      const { position, size } = el
      minX = Math.min(minX, position.x)
      minY = Math.min(minY, position.y)
      maxX = Math.max(maxX, position.x + size.width)
      maxY = Math.max(maxY, position.y + size.height)
    }

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    }
  }

  /**
   * Convert screen coordinates to world coordinates
   */
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    const { translate, scale } = this.transform
    return {
      x: (screenX - translate.x) / scale,
      y: (screenY - translate.y) / scale,
    }
  }

  /**
   * Convert world coordinates to screen coordinates
   */
  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    const { translate, scale } = this.transform
    return {
      x: worldX * scale + translate.x,
      y: worldY * scale + translate.y,
    }
  }
}
